// File: src/pages/organizations/InviteMembers.js
// Route: /organization/:orgId/invite

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/authContext';

const InviteMembers = ({ embedded = false, onInvitationSent }) => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const functions = getFunctions();

  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite form state
  const [email, setEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load org, members, and pending invitations in parallel
      const [orgSnap, membersSnap, invitesSnap] = await Promise.all([
        getDoc(doc(db, 'organizations', orgId)),
        getDocs(collection(db, 'organizations', orgId, 'members')),
        getDocs(query(
          collection(db, 'organizations', orgId, 'invitations'),
          where('status', '==', 'pending')
        )),
      ]);

      if (orgSnap.exists()) {
        setOrganization({ id: orgSnap.id, ...orgSnap.data() });
      }

      // Load member details
      const membersList = [];
      for (const memberDoc of membersSnap.docs) {
        const memberData = memberDoc.data();
        const userDoc = await getDoc(doc(db, 'users', memberDoc.id));
        membersList.push({
          userId: memberDoc.id,
          ...memberData,
          userName: userDoc.exists() ? userDoc.data().name : 'Unknown',
          userEmail: userDoc.exists() ? userDoc.data().email : '',
        });
      }
      setMembers(membersList);

      // Pending invitations
      const invites = invitesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      invites.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bTime - aTime;
      });
      setPendingInvitations(invites);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // License calculations
  const license = organization?.license || {};
  const totalLicenses = license.disciplemakers ?? license.seats ?? 0;
  const usedLicenses = license.disciplemakersUsed ?? license.seatsUsed ?? 0;
  const availableLicenses = Math.max(0, totalLicenses - usedLicenses);
  const effectiveAvailable = Math.max(0, availableLicenses - pendingInvitations.length);
  const hasLicense = license && (
    license.status === 'active' ||
    license.status === 'trialing' ||
    license.platformSubscriptionId ||
    license.stripeSubscriptionId
  );

  const searchUser = async () => {
    if (!email.trim()) return;
    setSearching(true);
    setError('');
    setSearchResult(null);
    setSuccessMessage('');
    try {
      // Check for existing pending invitation
      const inviteQ = query(
        collection(db, 'organizations', orgId, 'invitations'),
        where('email', '==', email.toLowerCase()),
        where('status', '==', 'pending')
      );
      const inviteSnap = await getDocs(inviteQ);
      if (!inviteSnap.empty) {
        setSearchResult({ type: 'pending-invite' });
        setSearching(false);
        return;
      }

      // Search for user
      const userQ = query(collection(db, 'users'), where('email', '==', email.toLowerCase()));
      const userSnap = await getDocs(userQ);

      if (userSnap.empty) {
        setSearchResult({ type: 'new-user', email: email.toLowerCase() });
      } else {
        const userData = userSnap.docs[0].data();
        const isMember = members.some(m => m.userEmail?.toLowerCase() === email.toLowerCase() || m.userId === userData.uid);
        if (isMember) {
          setSearchResult({ type: 'already-member', user: userData });
        } else {
          setSearchResult({ type: 'found', user: userData });
        }
      }
    } catch (err) {
      setError('Error searching for user');
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const sendInvitation = async () => {
    setSending(true);
    setError('');
    try {
      // Check license availability
      if (hasLicense && effectiveAvailable <= 0) {
        setError(`No licenses available. ${usedLicenses} allocated + ${pendingInvitations.length} pending = all ${totalLicenses} seats accounted for.`);
        setSending(false);
        return;
      }

      const inviteRef = doc(collection(db, 'organizations', orgId, 'invitations'));

      await runTransaction(db, async (transaction) => {
        // Create invitation
        transaction.set(inviteRef, {
          email: email.toLowerCase(),
          organizationId: orgId,
          organizationName: organization?.name || '',
          status: 'pending',
          createdAt: serverTimestamp(),
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          invitedBy: currentUser.uid,
          inviterName: currentUser.displayName || 'Organization Admin',
          licenseReserved: !!hasLicense,
          licenseStatus: hasLicense ? 'held' : 'none',
        });

        // Create license reservation
        if (hasLicense) {
          const reservationRef = doc(db, 'organizations', orgId, 'licenseReservations', inviteRef.id);
          transaction.set(reservationRef, {
            inviteId: inviteRef.id,
            email: email.toLowerCase(),
            status: 'held',
            reservedAt: serverTimestamp(),
            reservedBy: currentUser.uid,
            reservedByName: currentUser.displayName || 'Organization Admin',
          });
        }
      });

      // Mirror invite to invited user's subcollection (if user exists in system)
      try {
        const userQ = query(collection(db, 'users'), where('email', '==', email.toLowerCase()));
        const userSnap = await getDocs(userQ);
        if (!userSnap.empty) {
          const targetUserId = userSnap.docs[0].id;
          await setDoc(doc(db, 'users', targetUserId, 'invitations', inviteRef.id), {
            email: email.toLowerCase(),
            organizationId: orgId,
            organizationName: organization?.name || '',
            status: 'pending',
            createdAt: serverTimestamp(),
            expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            invitedBy: currentUser.uid,
            inviterName: currentUser.displayName || 'Organization Admin',
            licenseReserved: !!hasLicense,
            licenseStatus: hasLicense ? 'held' : 'none',
            inviteId: inviteRef.id,
          });
        }
      } catch (mirrorErr) {
        console.warn('Could not mirror invite to user subcollection:', mirrorErr);
      }

      // Send email
      try {
        const sendInvitationEmail = httpsCallable(functions, 'sendInvitationEmail');
        await sendInvitationEmail({
          recipientEmail: email.toLowerCase(),
          inviteId: inviteRef.id,
          organizationId: orgId,
          organizationName: organization?.name || '',
          inviterName: currentUser.displayName || 'Organization Admin',
        });
      } catch (emailErr) {
        console.warn('Email send failed (invitation still created):', emailErr);
      }

      setSuccessMessage(`Invitation sent to ${email}${hasLicense ? ' — license seat held' : ''}`);
      setEmail('');
      setSearchResult(null);

      // Refresh pending invitations
      await loadData();
      onInvitationSent?.();
    } catch (err) {
      setError(`Error: ${err.message || 'Failed to create invitation'}`);
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const resendInvitation = async (inviteId, inviteEmail) => {
    setProcessing(`resend-${inviteId}`);
    try {
      const sendInvitationEmail = httpsCallable(functions, 'sendInvitationEmail');
      await sendInvitationEmail({
        recipientEmail: inviteEmail,
        inviteId,
        organizationId: orgId,
        organizationName: organization?.name || '',
        inviterName: currentUser.displayName || 'Organization Admin',
      });
      setSuccessMessage(`Invitation resent to ${inviteEmail}`);
    } catch (err) {
      alert(`Error resending: ${err.message || 'Failed to resend invitation email'}`);
      console.error(err);
    } finally {
      setProcessing(null);
    }
  };

  const revokeInvitation = async (inviteId, inviteEmail) => {
    if (!window.confirm(`Revoke invitation to ${inviteEmail}? The held license seat will be released.`)) return;
    setProcessing(inviteId);
    try {
      await runTransaction(db, async (transaction) => {
        const inviteRef = doc(db, 'organizations', orgId, 'invitations', inviteId);
        const snap = await transaction.get(inviteRef);
        if (!snap.exists() || snap.data().status !== 'pending') {
          throw new Error('Invitation is no longer pending.');
        }
        transaction.update(inviteRef, {
          status: 'canceled',
          canceledAt: serverTimestamp(),
          canceledBy: currentUser.uid,
        });
        const reservationRef = doc(db, 'organizations', orgId, 'licenseReservations', inviteId);
        transaction.delete(reservationRef);
      });

      // Also update the user-side mirror so the invite disappears from their app
      try {
        const userQ = query(collection(db, 'users'), where('email', '==', inviteEmail.toLowerCase()));
        const userSnap = await getDocs(userQ);
        if (!userSnap.empty) {
          const targetUserId = userSnap.docs[0].id;
          await updateDoc(doc(db, 'users', targetUserId, 'invitations', inviteId), {
            status: 'canceled',
            canceledAt: serverTimestamp(),
            canceledBy: currentUser.uid,
          });
        }
      } catch (mirrorErr) {
        console.warn('Could not update user-side invite mirror:', mirrorErr);
      }

      await loadData();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <p className="text-red-600">Organization not found.</p>
      </div>
    );
  }

  return (
    <div className={embedded ? 'max-w-2xl' : 'container mx-auto p-6 max-w-2xl'}>
      {/* Header */}
      <div className="mb-6">
        {!embedded && (
          <button
            onClick={() => navigate(`/organization/${orgId}/subscription`)}
            className="text-blue-600 hover:text-blue-800 text-sm mb-3 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Subscription
          </button>
        )}
        <h1 className={embedded ? 'text-xl font-bold text-gray-900' : 'text-2xl font-bold text-gray-900'}>Invite Members</h1>
        <p className="text-gray-500 mt-1">{organization.name}</p>
      </div>

      {/* License Status Card */}
      {hasLicense && (
        <div className="bg-white border rounded-lg p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-800">License Availability</h2>
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${
              effectiveAvailable > 0
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {effectiveAvailable} available
            </span>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2.5 flex overflow-hidden mb-2">
            {totalLicenses > 0 && (
              <>
                <div
                  className="h-2.5 bg-blue-600"
                  style={{ width: `${(usedLicenses / totalLicenses) * 100}%` }}
                />
                {pendingInvitations.length > 0 && (
                  <div
                    className="h-2.5 bg-yellow-400"
                    style={{ width: `${Math.min((pendingInvitations.length / totalLicenses) * 100, 100 - (usedLicenses / totalLicenses) * 100)}%` }}
                  />
                )}
              </>
            )}
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>{usedLicenses} allocated{pendingInvitations.length > 0 ? ` · ${pendingInvitations.length} held` : ''}</span>
            <span>{totalLicenses} total seats</span>
          </div>
        </div>
      )}

      {/* Invite Form */}
      <div className="bg-white border rounded-lg p-5 mb-6">
        <h2 className="font-semibold text-gray-800 mb-4">Send Invitation</h2>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
        )}

        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setSearchResult(null); setSuccessMessage(''); }}
            onKeyDown={(e) => e.key === 'Enter' && email.trim() && searchUser()}
            placeholder="Enter email address"
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={searchUser}
            disabled={searching || !email.trim()}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {/* Search results */}
        {searchResult && (
          <div className="mt-4">
            {searchResult.type === 'pending-invite' && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                <p className="font-medium">Pending invitation exists</p>
                <p className="text-sm mt-1">An invitation has already been sent to this email.</p>
              </div>
            )}

            {searchResult.type === 'already-member' && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                <p className="font-medium">{searchResult.user.name || email} is already a member.</p>
              </div>
            )}

            {searchResult.type === 'found' && (
              <div className="p-4 bg-white border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-lg">
                      {(searchResult.user.name || email)[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{searchResult.user.name}</p>
                      <p className="text-sm text-gray-500">{email}</p>
                    </div>
                  </div>
                  <button
                    onClick={sendInvitation}
                    disabled={sending || (hasLicense && effectiveAvailable <= 0)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium text-sm"
                  >
                    {sending ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
                {hasLicense && effectiveAvailable > 0 && (
                  <p className="text-xs text-gray-500 mt-2">A license seat will be held until they accept.</p>
                )}
              </div>
            )}

            {searchResult.type === 'new-user' && (
              <div className="p-4 bg-white border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">New user</p>
                      <p className="text-sm text-gray-500">{email}</p>
                    </div>
                  </div>
                  <button
                    onClick={sendInvitation}
                    disabled={sending || (hasLicense && effectiveAvailable <= 0)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium text-sm"
                  >
                    {sending ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  No account found. They'll receive an email invitation to join.
                  {hasLicense && effectiveAvailable > 0 && ' A license seat will be held.'}
                </p>
              </div>
            )}

            {hasLicense && effectiveAvailable <= 0 && searchResult.type !== 'pending-invite' && searchResult.type !== 'already-member' && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                No licenses available. Upgrade your subscription to invite more members.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="bg-white border rounded-lg p-5">
          <h2 className="font-semibold text-gray-800 mb-4">
            Pending Invitations
            <span className="ml-2 text-sm font-normal text-gray-500">({pendingInvitations.length})</span>
          </h2>
          <div className="space-y-2">
            {pendingInvitations.map(invite => {
              const inviteDate = invite.createdAt?.toDate ? invite.createdAt.toDate() : new Date(invite.createdAt || 0);
              return (
                <div key={invite.id} className="flex items-center justify-between p-3 border border-yellow-200 rounded-lg bg-yellow-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-yellow-400 text-yellow-900 flex items-center justify-center text-xs font-bold">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-yellow-900 text-sm">{invite.email}</div>
                      <div className="text-xs text-yellow-700">
                        Sent {inviteDate.toLocaleDateString()}
                        {invite.inviterName && ` by ${invite.inviterName}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasLicense && (
                      <span className="text-xs font-medium px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full border border-yellow-300">
                        Seat Held
                      </span>
                    )}
                    <button
                      onClick={() => resendInvitation(invite.id, invite.email)}
                      disabled={processing === `resend-${invite.id}`}
                      className="px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                    >
                      {processing === `resend-${invite.id}` ? '...' : 'Resend'}
                    </button>
                    <button
                      onClick={() => revokeInvitation(invite.id, invite.email)}
                      disabled={processing === invite.id}
                      className="px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                    >
                      {processing === invite.id ? '...' : 'Revoke'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteMembers;
